import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {useNavigate} from '@github-ui/react-router'
import {getIconColorClassName, getIconComponent} from '@github-ui/saved-views'
import {CopyIcon, KebabHorizontalIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Heading, IconButton, useConfirm} from '@primer/react'

import type {SavedView} from '../../components/views/types'
import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {useDeleteSavedViewMutation} from '../../hooks/use-delete-saved-view-mutation'
import {useDuplicateSavedViewMutation} from '../../hooks/use-duplicate-saved-view-mutation'
import styles from './SavedViewHeader.module.css'

function ViewHeaderIcon({icon: Icon, color}: {icon: ReturnType<typeof getIconComponent>; color: string}) {
  return <Icon size={16} className={`${styles.titleIcon} ${getIconColorClassName(color)}`} />
}

interface SavedViewHeaderProps {
  savedView: SavedView
  owner: string
  repo: string
}

export function SavedViewHeader({savedView, owner, repo}: SavedViewHeaderProps) {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const {mutate: deleteView, isPending: isDeleting} = useDeleteSavedViewMutation({owner, repo})
  const {mutate: duplicateView, isPending: isDuplicating} = useDuplicateSavedViewMutation({owner, repo})

  const isMutating = isDeleting || isDuplicating

  const handleDuplicate = () =>
    duplicateView(
      {view: savedView},
      {
        onSuccess: ({savedView: newView}) => {
          navigate(`/${owner}/${repo}/issues/views/${newView.id}`)
        },
      },
    )

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: LABELS.views.deleteTitle,
      content: LABELS.views.deleteContent(savedView.name),
      confirmButtonContent: LABELS.views.deleteConfirmationButton,
      confirmButtonType: 'danger',
    })
    if (!confirmed) return
    deleteView(
      {id: savedView.id},
      {
        onSuccess: () => navigate(`/${owner}/${repo}/issues/views`),
      },
    )
  }

  return (
    <div className={styles.titleWrapper}>
      <div className={styles.header}>
        <div className={styles.titleArea}>
          <ViewHeaderIcon icon={getIconComponent(savedView.icon)} color={savedView.color} />
          <Heading as="h1" className={styles.title}>
            {savedView.name}
          </Heading>
        </div>
        <div className={styles.actionsGrp}>
          <CreateIssueButton
            label={BUTTON_LABELS.newIssue}
            navigate={navigate}
            optionConfig={{
              issueCreateArguments: {
                repository: {owner, name: repo},
              },
              showRepositoryPicker: false,
              showFullScreenButton: true,
              canBypassTemplateSelection: true,
              navigate,
            }}
          />
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label={`${savedView.name} options`} disabled={isMutating} />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="small">
              <ActionList>
                <ActionList.Item disabled>
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit view
                </ActionList.Item>
                <ActionList.Item onSelect={handleDuplicate} disabled={isMutating}>
                  <ActionList.LeadingVisual>
                    <CopyIcon />
                  </ActionList.LeadingVisual>
                  Duplicate
                </ActionList.Item>
                <ActionList.Item onSelect={handleDelete} disabled={isMutating} variant="danger">
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  Delete
                </ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      </div>
      {savedView.description && <p className={styles.description}>{savedView.description}</p>}
    </div>
  )
}
